"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _react = _interopRequireWildcard(require("react"));
var _utils = require("../../utils");
var _utils2 = require("./utils");
var _react2 = require("@emotion/react");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
/*
The MIT License (MIT)

Copyright (c) 2014 Call-Em-All

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.

Forked from the mui-base github repo (https://github.com/mui/material-ui/blob/next/packages/mui-base/src/FocusTrap/FocusTrap.tsx)
*/

/**
 * Utility that locks focus inside the component.
 */const FocusTrap = _ref => {
  let {
    active,
    children,
    clickOutsideDeactivates = false,
    disableAutoFocus = false,
    disableEnforceFocus = false,
    disableRestoreFocus = false,
    getTabbable = _utils2.defaultGetTabbable,
    isEnabled = _utils2.defaultIsEnabled,
    onDeactivate
  } = _ref;
  const ignoreNextEnforceFocus = (0, _react.useRef)(false);
  const sentinelStart = (0, _react.useRef)(null);
  const sentinelEnd = (0, _react.useRef)(null);
  const nodeToRestore = (0, _react.useRef)(null);
  const reactFocusEventTarget = (0, _react.useRef)(null);
  // This variable is useful when disableAutoFocus is true.
  // It waits for the active element to move into the component to activate.
  const activated = (0, _react.useRef)(false);
  const hasLivedEnough = (0, _react.useRef)(false);
  const rootRef = (0, _react.useRef)(null);
  const handleRef = (0, _utils.mergeRefs)(children.ref, rootRef);
  const lastKeydown = (0, _react.useRef)(null);
  const clickOutsideDeactivatesIsFunc = typeof clickOutsideDeactivates === 'function';
  const handleClickOutside = e => {
    if (!e) {
      return false;
    }
    const eventPath = e.composedPath ? e.composedPath() : [];
    // if active doc element is outside but actually clicked inside
    if (eventPath.includes(rootRef.current)) {
      var _rootRef$current;
      rootRef === null || rootRef === void 0 || (_rootRef$current = rootRef.current) === null || _rootRef$current === void 0 || _rootRef$current.focus();
    } else {
      return true;
    }
    return false;
  };
  (0, _react.useLayoutEffect)(() => {
    (0, _utils.debounce)(() => {
      hasLivedEnough.current = true;
    })();
  }, []);
  (0, _react.useEffect)(() => {
    // We might render an empty child.
    if (!active || !rootRef.current) {
      return;
    }
    activated.current = !disableAutoFocus;
  }, [active, disableAutoFocus]);
  (0, _react.useEffect)(() => {
    // We might render an empty child.
    if (!active || !rootRef.current) {
      return;
    }
    const doc = rootRef.current && rootRef.current.ownerDocument || document;
    if (!rootRef.current.contains(doc.activeElement)) {
      if (!rootRef.current.hasAttribute('tabIndex')) {
        rootRef.current.setAttribute('tabIndex', '-1');
      }
      if (!disableAutoFocus && activated.current) {
        rootRef.current.focus();
      }
    }
    return () => {
      if (hasLivedEnough.current && !disableRestoreFocus) {
        // In IE11 it is possible for document.activeElement to be null resulting
        // in nodeToRestore.current being null.
        // Not all elements in IE11 have a focus method.
        // Once IE11 support is dropped the focus() call can be unconditional.
        if (nodeToRestore.current && nodeToRestore.current.focus) {
          ignoreNextEnforceFocus.current = true;
          nodeToRestore.current.focus();
        }
        nodeToRestore.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
  (0, _react.useEffect)(() => {
    var _rootRef$current2;
    // We might render an empty child.
    if (!active || !rootRef.current) {
      return;
    }
    const doc = (rootRef === null || rootRef === void 0 || (_rootRef$current2 = rootRef.current) === null || _rootRef$current2 === void 0 ? void 0 : _rootRef$current2.ownerDocument) || document;
    const loopFocus = nativeEvent => {
      var _doc$activeElement;
      lastKeydown.current = nativeEvent;

      // Special handling to prevent "deactivate" when "downshift" loses focus in multiselect
      // TODO: check if possible to remove after getting rid of "downshift"
      if (nativeEvent.key === _utils.EventKeys.TAB && ((_doc$activeElement = doc.activeElement) === null || _doc$activeElement === void 0 ? void 0 : _doc$activeElement.tagName) === 'UL' && doc.activeElement.dataset.testComponent === 'StencilSelectList') {
        var _doc$activeElement2;
        const triggerRef = (_doc$activeElement2 = doc.activeElement) === null || _doc$activeElement2 === void 0 || (_doc$activeElement2 = _doc$activeElement2.closest('[data-test-component="StencilSelect"]')) === null || _doc$activeElement2 === void 0 ? void 0 : _doc$activeElement2.querySelector('[data-test-component="StencilSelectTrigger"]');
        hasLivedEnough.current = false;
        (0, _utils.debounce)(() => {
          if (!(0, _utils.isTabbable)(doc.activeElement)) {
            triggerRef === null || triggerRef === void 0 || triggerRef.focus();
          }
          hasLivedEnough.current = true;
        }, 50)();
      }
      if (disableEnforceFocus || !isEnabled() || nativeEvent.key === _utils.EventKeys.TAB) {
        return;
      }
      if (nativeEvent.key === _utils.EventKeys.ESCAPE || nativeEvent.key === 'Esc' || nativeEvent.keyCode === _utils.EventKeyCodes.ESCAPE) {
        nativeEvent.preventDefault();
        nativeEvent.stopPropagation();
        onDeactivate === null || onDeactivate === void 0 || onDeactivate();
      }

      // Make sure the next tab starts from the right place.
      // doc.activeElement refers to the origin.
      if (doc.activeElement === rootRef.current && nativeEvent.shiftKey) {
        // We need to ignore the next contain as
        // it will try to move the focus back to the rootRef element.
        ignoreNextEnforceFocus.current = true;
        if (sentinelEnd.current) {
          sentinelEnd.current.focus();
        }
      }
    };
    const checkClick = function (e) {
      // Cleanup functions are executed lazily in React 17.
      // Contain can be called between the component being unmounted and its cleanup function being run.
      if (rootRef.current === null || !hasLivedEnough.current) {
        return;
      }
      if (!doc.hasFocus() || !isEnabled() || ignoreNextEnforceFocus.current) {
        ignoreNextEnforceFocus.current = false;
        return;
      }

      // The focus is already inside
      if (rootRef.current.contains(doc.activeElement)) {
        return;
      }
      if (!activated.current) {
        return;
      }
      if (clickOutsideDeactivatesIsFunc && clickOutsideDeactivates(e) || !clickOutsideDeactivatesIsFunc && clickOutsideDeactivates && handleClickOutside(e)) {
        onDeactivate === null || onDeactivate === void 0 || onDeactivate();
      }
    };
    const contain = event => {
      // Cleanup functions are executed lazily in React 17.
      // Contain can be called between the component being unmounted and its cleanup function being run.
      if (rootRef.current === null) {
        return;
      }
      if (!doc.hasFocus() || !isEnabled() || ignoreNextEnforceFocus.current) {
        ignoreNextEnforceFocus.current = false;
        return;
      }

      // The focus is already inside
      if (rootRef.current.contains(doc.activeElement)) {
        return;
      }

      // The disableEnforceFocus is set and the focus is outside of the focus trap (and sentinel nodes)
      if (disableEnforceFocus && doc.activeElement !== sentinelStart.current && doc.activeElement !== sentinelEnd.current) {
        return;
      }

      // if the focus event is not coming from inside the children's react tree, reset the refs
      if (doc.activeElement !== reactFocusEventTarget.current) {
        reactFocusEventTarget.current = null;
      } else if (reactFocusEventTarget.current !== null) {
        return;
      }
      if (!activated.current || !hasLivedEnough.current) {
        return;
      }
      let tabbable = [];
      if (doc.activeElement === sentinelStart.current || doc.activeElement === sentinelEnd.current) {
        tabbable = getTabbable(rootRef.current);
      }

      // one of the sentinel nodes was focused, so move the focus
      // to the first/last tabbable element inside the focus trap
      if (tabbable.length > 0) {
        var _lastKeydown$current, _lastKeydown$current2;
        const isShiftTab = Boolean(((_lastKeydown$current = lastKeydown.current) === null || _lastKeydown$current === void 0 ? void 0 : _lastKeydown$current.shiftKey) && ((_lastKeydown$current2 = lastKeydown.current) === null || _lastKeydown$current2 === void 0 ? void 0 : _lastKeydown$current2.key) === _utils.EventKeys.TAB);
        const focusNext = tabbable[0];
        const focusPrevious = tabbable[tabbable.length - 1];
        if (typeof focusNext !== 'string' && typeof focusPrevious !== 'string') {
          if (isShiftTab) {
            focusPrevious.focus();
          } else {
            focusNext.focus();
          }
        }
        // no tabbable elements in the trap focus or the focus was outside of the focus trap
      } else if (clickOutsideDeactivatesIsFunc && clickOutsideDeactivates(event) || !clickOutsideDeactivatesIsFunc && clickOutsideDeactivates && handleClickOutside(event)) {
        onDeactivate === null || onDeactivate === void 0 || onDeactivate();
      } else {
        rootRef.current.focus();
      }
    };
    const clickEventName = (0, _utils2.getClickEventName)();
    doc.addEventListener(clickEventName, checkClick);
    doc.addEventListener('focusin', contain);
    doc.addEventListener('keydown', loopFocus, true);
    return () => {
      doc.removeEventListener(clickEventName, checkClick);
      doc.removeEventListener('focusin', contain);
      doc.removeEventListener('keydown', loopFocus, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, clickOutsideDeactivates, disableAutoFocus, disableEnforceFocus, disableRestoreFocus, getTabbable, isEnabled]);
  const onFocus = event => {
    if (nodeToRestore.current === null) {
      nodeToRestore.current = event.relatedTarget;
    }
    activated.current = true;
    reactFocusEventTarget.current = event.target;
    const childrenPropsHandler = children.props.onFocus;
    if (childrenPropsHandler) {
      childrenPropsHandler(event);
    }
  };
  const commonProps = {
    onFocus: event => {
      if (nodeToRestore.current === null) {
        nodeToRestore.current = event.relatedTarget;
      }
      activated.current = true;
    },
    tabIndex: active ? 0 : -1
  };
  return (0, _react2.jsx)(_react.default.Fragment, null, (0, _react2.jsx)("div", (0, _extends2.default)({}, commonProps, {
    ref: sentinelStart
  })), /*#__PURE__*/_react.default.cloneElement(children, {
    ref: handleRef,
    onFocus
  }), (0, _react2.jsx)("div", (0, _extends2.default)({}, commonProps, {
    ref: sentinelEnd
  })));
};
var _default = exports.default = FocusTrap;